//
// # User Preferences
//
// Centralized, reactive store for user-facing IDE preferences.
// Persisted to localStorage as a single versioned JSON blob so that values
// survive page reloads. Persistence failures (private mode, quota, parse
// errors) are swallowed — the store always has valid in-memory defaults.
//

import typia from "typia";
import { validate as v } from "CoreUtils";
import { Theme, applyThemeToDom } from "EditorCore";

// The three user-facing theme modes.
export type IdeThemeMode = "light" | "dark" | "auto";

export const IdeThemeMode = {
  Light: "light",
  Dark: "dark",
  Auto: "auto",
} as const;

// Grid-line intensity: High shows bolder grid lines (the old high-contrast
// grid colors), Normal shows subtler lines (the old default grid colors).
export type GridIntensity = "normal" | "high";

export const GridIntensity = {
  Normal: "normal",
  High: "high",

  // Toggle between the two intensity levels.
  other: (intensity: GridIntensity): GridIntensity =>
    intensity === "high" ? "normal" : "high",
} as const;

export interface UserPreferences {
  readonly themeMode: IdeThemeMode;
  readonly gridIntensity: GridIntensity;
  // Trackpad mode (toggled with Shift+T) — changes scroll/zoom input
  // interpretation. Applied across all open editors.
  readonly trackpadMode: boolean;
}

// Runtime validator for the in-memory preferences shape.  Used at the
// localStorage boundary; in-memory mutations are already statically typed.
export const UserPreferencesValidator = {
  validator: new v.Validator(typia.createValidate<UserPreferences>()),
};

// localStorage key for the persisted preferences blob.
const STORAGE_KEY = "genalpha:prefs";

// Bump this when the on-disk shape changes incompatibly. Older blobs whose
// version doesn't match are discarded and replaced with defaults rather
// than being silently coerced.
const STORAGE_VERSION = 1;

// The shape we actually write to localStorage. Wrapping the prefs in a
// versioned envelope leaves room to migrate the inner shape later.
interface PersistedPreferences {
  readonly version: number;
  readonly prefs: UserPreferences;
}

type Listener = () => void;

// Resolve the OS / browser preferred color scheme right now.
function osPrefersLight(): boolean {
  return !window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export class UserPreferencesStore {
  private _themeMode: IdeThemeMode = IdeThemeMode.Auto;
  private _gridIntensity: GridIntensity = GridIntensity.Normal;
  private _trackpadMode: boolean = false;
  private readonly listeners: Set<Listener> = new Set();
  private readonly mediaQuery = window.matchMedia(
    "(prefers-color-scheme: dark)"
  );

  constructor() {
    // Re-apply when the OS preference changes (only matters in "auto" mode).
    this.mediaQuery.addEventListener("change", this.onOsThemeChange);
    // Restore from localStorage before the first paint. Falls back silently
    // to defaults on missing / invalid / unparseable data.
    this.loadFromStorage();
    // Apply the theme immediately so that `data-theme` is set on <html>
    // before any DOM is painted. This avoids a brief FOUC between module
    // load and the Workspace constructor.
    this.applyTheme();
  }

  // This dispose function isn't needed in practice as we're a singleton,
  // but keep it around for completeness perhaps future testing.
  dispose(): void {
    this.mediaQuery.removeEventListener("change", this.onOsThemeChange);
    this.listeners.clear();
  }

  // --- Public accessors ------------------------------------------------

  get(): Readonly<UserPreferences> {
    return {
      themeMode: this._themeMode,
      gridIntensity: this._gridIntensity,
      trackpadMode: this._trackpadMode,
    };
  }

  // Resolve Auto to the concrete Light or Dark value by consulting the OS
  // preference. The return is always Light or Dark — never Auto — but the
  // type stays IdeThemeMode for API consistency.
  resolvedTheme(): IdeThemeMode {
    if (this._themeMode === IdeThemeMode.Auto) {
      return osPrefersLight() ? IdeThemeMode.Light : IdeThemeMode.Dark;
    }
    return this._themeMode;
  }

  // The next mode in the Light → Dark → Auto cycle.
  static nextThemeMode(current: IdeThemeMode): IdeThemeMode {
    switch (current) {
      case IdeThemeMode.Light:
        return IdeThemeMode.Dark;
      case IdeThemeMode.Dark:
        return IdeThemeMode.Auto;
      case IdeThemeMode.Auto:
        return IdeThemeMode.Light;
      default:
        return IdeThemeMode.Auto;
    }
  }

  // --- Mutators --------------------------------------------------------

  setThemeMode(mode: IdeThemeMode): void {
    if (mode === this._themeMode) return;
    this._themeMode = mode;
    this.persist();
    this.applyTheme();
    this.notify();
    console.log(`[UserPreferences] themeMode = ${mode}`);
  }

  setGridIntensity(intensity: GridIntensity): void {
    if (intensity === this._gridIntensity) return;
    this._gridIntensity = intensity;
    this.persist();
    this.notify();
    console.log(`[UserPreferences] gridIntensity = ${intensity}`);
  }

  setTrackpadMode(enabled: boolean): void {
    if (enabled === this._trackpadMode) return;
    this._trackpadMode = enabled;
    this.persist();
    this.notify();
    console.log(`[UserPreferences] trackpadMode = ${enabled}`);
  }

  // --- Subscriptions ---------------------------------------------------

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  // --- Theme application -----------------------------------------------

  // Apply the resolved theme to the document and EditorCore.
  // Call once at startup and whenever the resolved value changes.
  applyTheme(): void {
    const resolved = this.resolvedTheme();
    // IDE CSS variables switch via [data-theme] selector.
    document.documentElement.setAttribute("data-theme", resolved);
    // EditorCore's global theme (viewport background, overlay colors, etc.).
    const coreTheme = resolved === IdeThemeMode.Dark ? Theme.Dark : Theme.Light;
    Theme.set(coreTheme);
    applyThemeToDom(coreTheme);
  }

  // --- Internals -------------------------------------------------------

  private onOsThemeChange = (): void => {
    if (this._themeMode === IdeThemeMode.Auto) {
      this.applyTheme();
      this.notify();
    }
  };

  private notify(): void {
    for (const fn of this.listeners) {
      fn();
    }
  }

  // --- Persistence -----------------------------------------------------

  // Read the persisted blob, validate it, and update in-memory state.
  // On any failure (no entry, throws, bad JSON, wrong version, validator
  // rejection) the in-memory defaults are kept and any corrupt entry is
  // cleared so we don't keep tripping over it.
  private loadFromStorage(): void {
    let raw: string | null;
    try {
      raw = localStorage.getItem(STORAGE_KEY);
    } catch {
      // localStorage can throw in private browsing or with disabled storage.
      return;
    }
    if (raw === null) return;

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      // Corrupt JSON.  Drop it and start fresh.
      this.clearStorage();
      return;
    }

    // Envelope shape check: { version, prefs }
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      !("version" in parsed) ||
      !("prefs" in parsed)
    ) {
      console.warn("[UserPreferences] Discarding malformed stored prefs");
      this.clearStorage();
      return;
    }
    const envelope = parsed as { version: unknown; prefs: unknown };
    if (envelope.version !== STORAGE_VERSION) {
      // Future: migrate older versions.  For now, defaults win.
      console.warn(
        `[UserPreferences] Discarding prefs at unknown version ${String(
          envelope.version
        )}`
      );
      this.clearStorage();
      return;
    }

    const validated = UserPreferencesValidator.validator.validate(
      envelope.prefs
    );
    if (validated.err) {
      console.warn(
        "[UserPreferences] Discarding stored prefs that failed validation",
        validated.val
      );
      this.clearStorage();
      return;
    }
    this._themeMode = validated.val.themeMode;
    this._gridIntensity = validated.val.gridIntensity;
    this._trackpadMode = validated.val.trackpadMode;
    this.notify(); // Apply the new values immediately. Note: on first load, we have no listeners.
    console.log("[UserPreferences] Loaded from localStorage:", validated.val);
  }

  // Write the current preferences to localStorage. Failures are swallowed
  // so that a flaky storage layer never breaks an in-app toggle.
  private persist(): void {
    const blob: PersistedPreferences = {
      version: STORAGE_VERSION,
      prefs: {
        themeMode: this._themeMode,
        gridIntensity: this._gridIntensity,
        trackpadMode: this._trackpadMode,
      },
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(blob));
    } catch {
      // Quota / private mode / disabled storage — nothing actionable here.
    }
  }

  private clearStorage(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Same swallow as persist().
    }
  }
}

// Singleton instance — import this from other IDE modules.
// Lives the entire lifetime of the IDE and is never disposed of.
export const userPreferences = new UserPreferencesStore();
